/**
 * Helpers purs pour les shifts récurrents (planning hebdo des heures de
 * travail). Partagés par la grille manager (`panels/organisation/ShiftsTab`) et
 * la vue perso (`panels/personnel/ShiftTab`).
 *
 * Modèle : un bloc = `weekdays` (1=Lun … 7=Dim) + `startMinute`/`endMinute`
 * (minutes depuis minuit). Si `endMinute <= startMinute`, le bloc traverse
 * minuit (overnight, ex. 18:00→02:00). Ces minutes se lisent TOUJOURS dans le
 * fuseau de l'agence (`FUSEAU_DES_SHIFTS`), jamais dans celui du poste.
 */

// ─── Le fuseau des shifts — un seul, et ce n'est PAS celui du poste ──────

/**
 * Fuseau d'ANCRAGE de toutes les heures de shift de l'agence.
 *
 * ⛔ **Le fuseau du poste n'entre plus jamais dans un shift** — ni dans la
 * décision (suis-je dans mon créneau ?), ni dans ce qui s'affiche. Une heure de
 * shift se lit en heure de Paris PARTOUT : le cockpit, les salons vocaux
 * Discord (« Vocal — Shift A (08h30–12h30) »), les rappels DM du bot Cerbère
 * (`SHIFT_MIN`, `TZ=Europe/Paris`) et la grille que Kayla pose disent alors le
 * même nombre. Décision de Kayla, 10-09-2026.
 *
 * 🩸 Ce qu'a coûté l'autre modèle. Le cockpit convertissait chaque heure dans
 * le fuseau du DEVICE ; le poste du titulaire du Shift A est réglé sur UTC.
 * Son écran annonçait donc 06:30–10:30 quand Discord annonçait 08h30–12h30,
 * et rien ne disait lequel des deux nombres était en quoi. Il a démarré son
 * shift à 08:30:16, 08:30:02 puis 08:11:11 **UTC** — à la seconde, quelqu'un
 * qui clique quand SON horloge affiche l'heure annoncée. Soit 10:30 Paris,
 * sur un créneau qui se referme à 12:30 : **2 h travaillées sur 4, trois jours
 * de suite**, sans une seule erreur de sa part et sans que rien ne rougisse.
 *
 * 🪤 Et le correctif d'avant (#865, gate sur l'heure du serveur) visait à côté :
 * il traitait une horloge fausse. Elle ne l'était pas — `lateMinutes`, calculé
 * par son propre navigateur, valait 110 sur un retard réel de 120, donc son
 * navigateur était à la bonne SECONDE. C'était le fuseau, pas l'heure. Garder
 * les deux : une horloge peut dériver ET un fuseau peut mentir.
 */
export const FUSEAU_DES_SHIFTS = "Europe/Paris";

/**
 * Fuseau dans lequel lire les minutes d'un bloc : le sien s'il en porte un,
 * sinon celui de l'agence. Les blocs `timezone: null` sont d'avant le
 * 23-06-2026 ; il n'en reste aucun en base (17 lignes, toutes `Europe/Paris`,
 * mesuré le 10-09-2026), mais le champ est optionnel au schéma et le repli doit
 * être une CONSTANTE, jamais le poste qui regarde.
 */
export function fuseauDuBloc(timezone: string | null | undefined): string {
  return timezone ?? FUSEAU_DES_SHIFTS;
}

/** Ordre d'affichage des jours : Lundi(1) → Dimanche(7). */
export const WEEKDAYS: number[] = [1, 2, 3, 4, 5, 6, 7];

/**
 * Date de référence pour un jour de semaine (1=Lun … 7=Dim), utilisée pour
 * produire les libellés localisés via Intl. 2024-01-01 est un LUNDI, donc
 * `new Date(2024, 0, d)` tombe sur le bon jour pour d ∈ [1..7]. Dates fixes →
 * pur (pas de `Date.now()`).
 */
function weekdayRefDate(weekday: number): Date {
  return new Date(2024, 0, weekday);
}

/** Libellé court localisé d'un jour (ex. "lun."). */
export function weekdayShort(weekday: number, locale: string): string {
  return new Intl.DateTimeFormat(locale, { weekday: "short" }).format(
    weekdayRefDate(weekday),
  );
}

/** Libellé long localisé d'un jour (ex. "lundi"). */
export function weekdayLong(weekday: number, locale: string): string {
  return new Intl.DateTimeFormat(locale, { weekday: "long" }).format(
    weekdayRefDate(weekday),
  );
}

/**
 * Formate un ensemble de jours en libellé compact et lisible :
 *   [1,2,3,4,5,6,7] → "lun. – dim."   (semaine complète)
 *   [1,2,3,4,5]     → "lun. – ven."   (plage contiguë)
 *   [6,7]           → "sam. – dim."
 *   [1,3,5]         → "lun., mer., ven." (liste)
 *   [4]             → "jeu."
 * Détecte UNE plage contiguë ; sinon liste séparée par des virgules.
 */
export function formatWeekdays(weekdays: number[], locale: string): string {
  const sorted = Array.from(new Set(weekdays)).sort((a, b) => a - b);
  if (sorted.length === 0) return "";
  if (sorted.length === 1) return weekdayShort(sorted[0]!, locale);

  const isContiguous = sorted.every(
    (d, i) => i === 0 || d === sorted[i - 1]! + 1,
  );
  if (isContiguous) {
    return `${weekdayShort(sorted[0]!, locale)} – ${weekdayShort(
      sorted[sorted.length - 1]!,
      locale,
    )}`;
  }
  return sorted.map((d) => weekdayShort(d, locale)).join(", ");
}

/** minutes depuis minuit → "HH:MM" (zéro-paddé). */
export function minutesToHHMM(min: number): string {
  const clamped = Math.max(0, Math.min(1439, Math.round(min)));
  const h = Math.floor(clamped / 60);
  const m = clamped % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

/** "HH:MM" → minutes depuis minuit. Retourne NaN si invalide. */
export function hhmmToMinutes(s: string): number {
  const m = /^(\d{1,2}):(\d{2})$/.exec(s.trim());
  if (!m) return NaN;
  const h = Number(m[1]);
  const min = Number(m[2]);
  if (h > 23 || min > 59) return NaN;
  return h * 60 + min;
}

/** true si le bloc traverse minuit (overnight). */
export function isOvernight(startMinute: number, endMinute: number): boolean {
  return endMinute <= startMinute;
}

/** "09:00 – 17:00" (ou "18:00 – 02:00" overnight) — libellé d'un créneau. */
export function slotRangeLabel(startMinute: number, endMinute: number): string {
  return `${minutesToHHMM(startMinute)} – ${minutesToHHMM(endMinute)}`;
}

/** Durée d'un créneau en minutes, overnight-aware (wrap à minuit). */
export function blockDurationMinutes(
  startMinute: number,
  endMinute: number,
): number {
  if (endMinute > startMinute) return endMinute - startMinute;
  // overnight (ou égal traité comme jour plein, mais égal est rejeté côté serveur)
  return 1440 - startMinute + endMinute;
}

/** Durée formatée "8h" ou "7h30". */
export function formatHours(totalMinutes: number): string {
  const safe = Math.max(0, Math.round(totalMinutes));
  const h = Math.floor(safe / 60);
  const m = safe % 60;
  return m === 0 ? `${h}h` : `${h}h${String(m).padStart(2, "0")}`;
}

/**
 * Total des heures HEBDOMADAIRES d'un ensemble de blocs : pour chaque bloc,
 * durée × nombre de jours où il s'applique. Pour l'en-tête « Heures / semaine ».
 */
export function weeklyMinutes(
  blocks: { weekdays: number[]; startMinute: number; endMinute: number }[],
): number {
  return blocks.reduce(
    (sum, b) =>
      sum + blockDurationMinutes(b.startMinute, b.endMinute) * b.weekdays.length,
    0,
  );
}

// ─── Pointage (clock-in) — heure LOCALE du device ────────────────────────

/** Jour de semaine LOCAL courant en convention 1=Lun … 7=Dim. */
export function localWeekdayToday(): number {
  const d = new Date().getDay(); // 0=Dim … 6=Sam
  return d === 0 ? 7 : d;
}

/** Minute du jour LOCALE courante (0–1439). */
export function localMinuteOfDay(): number {
  const now = new Date();
  return now.getHours() * 60 + now.getMinutes();
}

/**
 * Début prévu (minute la plus tôt) des blocs du membre pour un jour donné,
 * ou null si rien de prévu ce jour-là. Sert au retard au clock-in.
 */
export function scheduledStartForWeekday(
  blocks: { weekdays: number[]; startMinute: number }[],
  weekday: number,
): number | null {
  const starts = blocks
    .filter((b) => b.weekdays.includes(weekday))
    .map((b) => b.startMinute);
  return starts.length === 0 ? null : Math.min(...starts);
}

/** Compteur écoulé "h:mm:ss" (heures sans zéro-pad, minutes/secondes paddées). */
export function formatElapsed(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}
